import { Type, type Static } from "@sinclair/typebox";
import { UuidSchema } from "./identifiers.ts";

export const KNOWLEDGE_RETRIEVAL_SCHEMA_VERSION = "3.0" as const;
export const RETRIEVAL_ROUTES = [
  "raw_bm25",
  "raw_unit_ann",
  "statement_ann",
  "query_alias_ann",
  "leaf_ann"
] as const;
export type RetrievalRoute = (typeof RETRIEVAL_ROUTES)[number];
export type RetrievalDirectRoute = RetrievalRoute;

const IdentifierSchema = Type.String({ minLength: 1, maxLength: 256 });
const Sha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const PositiveIntegerSchema = Type.Integer({ minimum: 1 });
const PositiveNumberSchema = Type.Number({ exclusiveMinimum: 0 });
const RouteSchema = Type.Union(RETRIEVAL_ROUTES.map((route) => Type.Literal(route)));
const ChannelStatusSchema = Type.Union([
  Type.Literal("completed"),
  Type.Literal("empty"),
  Type.Literal("failed")
]);

export const RetrievalResultBudgetSchema = Type.Object(
  {
    maxItemsPerNeed: PositiveIntegerSchema,
    maxItemChars: PositiveIntegerSchema,
    maxTotalCharsPerNeed: PositiveIntegerSchema,
    maxDocumentsPerNeed: PositiveIntegerSchema,
    maxUnitsPerDocument: PositiveIntegerSchema
  },
  { additionalProperties: false }
);

const ChannelLimitsSchema = Type.Object(
  {
    rawBm25TopK: PositiveIntegerSchema,
    rawAnnCandidateK: PositiveIntegerSchema,
    statementAnnTopK: PositiveIntegerSchema,
    queryAliasAnnTopK: PositiveIntegerSchema,
    leafAnnTopK: PositiveIntegerSchema
  },
  { additionalProperties: false }
);

export const RetrievalExecutionConfigSchema = Type.Object(
  {
    probeRoutes: Type.Array(RouteSchema, {
      minItems: 1,
      maxItems: RETRIEVAL_ROUTES.length,
      uniqueItems: true
    }),
    targetedRoutes: Type.Array(RouteSchema, {
      minItems: 1,
      maxItems: RETRIEVAL_ROUTES.length,
      uniqueItems: true
    }),
    maxNeedsPerFollowUp: PositiveIntegerSchema,
    maxConcurrentNeeds: PositiveIntegerSchema,
    result: RetrievalResultBudgetSchema,
    channels: ChannelLimitsSchema,
    scoring: Type.Object(
      {
        routeAwareRrfK: PositiveIntegerSchema,
        rawBm25Weight: PositiveNumberSchema,
        rawUnitAnnWeight: PositiveNumberSchema,
        statementAnnWeight: PositiveNumberSchema,
        queryAliasAnnWeight: PositiveNumberSchema,
        leafAnnWeight: PositiveNumberSchema
      },
      { additionalProperties: false }
    ),
    timeouts: Type.Object(
      {
        requestMs: PositiveIntegerSchema
      },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);

export const RetrievalDeploymentHardCapsSchema = Type.Object(
  {
    maxRequestBytes: PositiveIntegerSchema,
    maxQueryChars: PositiveIntegerSchema,
    maxExcludeEvidenceIds: PositiveIntegerSchema,
    maxNeedsPerFollowUp: PositiveIntegerSchema,
    maxConcurrentNeeds: PositiveIntegerSchema,
    result: RetrievalResultBudgetSchema,
    channels: ChannelLimitsSchema,
    maxDependencyTimeoutMs: PositiveIntegerSchema
  },
  { additionalProperties: false }
);

export const RetrievalRuntimeConfigSchema = Type.Object(
  {
    deploymentHardCaps: RetrievalDeploymentHardCapsSchema
  },
  { additionalProperties: false }
);

const CommonRequestFields = {
  schemaVersion: Type.Literal(KNOWLEDGE_RETRIEVAL_SCHEMA_VERSION),
  requestId: IdentifierSchema,
  scope: Type.Object({ knowledgeBaseId: UuidSchema }, { additionalProperties: false }),
  originalQuery: Type.String({ minLength: 1, maxLength: 4096 }),
  excludeEvidenceIds: Type.Array(IdentifierSchema, { maxItems: 1024, uniqueItems: true }),
  resultBudget: RetrievalResultBudgetSchema
};

export const RetrievalProbeRequestSchema = Type.Object(
  { ...CommonRequestFields, purpose: Type.Literal("probe") },
  { additionalProperties: false }
);
export const RetrievalFollowUpRequestSchema = Type.Object(
  {
    ...CommonRequestFields,
    purpose: Type.Literal("follow_up"),
    needs: Type.Array(
      Type.Object(
        {
          needId: IdentifierSchema,
          query: Type.String({ minLength: 1, maxLength: 4096 })
        },
        { additionalProperties: false }
      ),
      { minItems: 1, maxItems: 64 }
    )
  },
  { additionalProperties: false }
);
export const RetrievalSearchRequestSchema = Type.Union([
  RetrievalProbeRequestSchema,
  RetrievalFollowUpRequestSchema
]);

export const RetrievalSourceSpanSchema = Type.Object(
  {
    startOffset: Type.Integer({ minimum: 0 }),
    endOffset: Type.Integer({ minimum: 1 }),
    sectionPath: Type.Optional(
      Type.Array(Type.String({ minLength: 1, maxLength: 512 }), { maxItems: 64 })
    )
  },
  { additionalProperties: false }
);

export const RetrievalCitationSchema = Type.Object(
  {
    sourceType: Type.Literal("internal_knowledge"),
    knowledgeBaseId: UuidSchema,
    documentId: UuidSchema,
    unitId: IdentifierSchema,
    title: Type.String({ minLength: 1, maxLength: 1024 }),
    sourceSpans: Type.Array(RetrievalSourceSpanSchema, { maxItems: 1024 })
  },
  { additionalProperties: false }
);

export const RetrievalEvidenceSchema = Type.Object(
  {
    evidenceId: IdentifierSchema,
    content: Type.String({ minLength: 1 }),
    contentHash: Sha256Schema,
    retrievedForNeedIds: Type.Array(IdentifierSchema, { maxItems: 64, uniqueItems: true }),
    matchedRoutes: Type.Array(RouteSchema, {
      minItems: 1,
      maxItems: RETRIEVAL_ROUTES.length,
      uniqueItems: true
    }),
    citation: RetrievalCitationSchema
  },
  { additionalProperties: false }
);

export const RetrievalChannelResultSchema = Type.Object(
  {
    route: RouteSchema,
    status: ChannelStatusSchema,
    hitCount: Type.Integer({ minimum: 0 }),
    errorCode: Type.Optional(Type.String({ minLength: 1, maxLength: 128 }))
  },
  { additionalProperties: false }
);

export const RetrievalSearchResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_RETRIEVAL_SCHEMA_VERSION),
    searchId: IdentifierSchema,
    status: Type.Union([Type.Literal("ok"), Type.Literal("no_evidence")]),
    knowledgeBaseId: UuidSchema,
    evidence: Type.Array(RetrievalEvidenceSchema, { maxItems: 1024 }),
    channelResults: Type.Array(RetrievalChannelResultSchema, { maxItems: RETRIEVAL_ROUTES.length }),
    durationMs: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);

export const RetrievalSearchTraceInputSchema = Type.Object(
  {
    query: Type.String({ minLength: 1, maxLength: 4096 })
  },
  { additionalProperties: false }
);

const RetrievalSearchTraceHitSchema = Type.Object(
  {
    needId: IdentifierSchema,
    rank: PositiveIntegerSchema,
    score: Type.Number(),
    evidenceId: IdentifierSchema,
    documentId: UuidSchema,
    unitId: IdentifierSchema,
    selected: Type.Boolean()
  },
  { additionalProperties: false }
);

const RetrievalSearchRouteTraceSchema = Type.Object(
  {
    route: RouteSchema,
    status: Type.Union([ChannelStatusSchema, Type.Literal("not_run")]),
    durationMs: Type.Integer({ minimum: 0 }),
    candidateHitCount: Type.Integer({ minimum: 0 }),
    currentHitCount: Type.Integer({ minimum: 0 }),
    items: Type.Array(RetrievalSearchTraceHitSchema, { maxItems: 200 }),
    truncated: Type.Boolean(),
    errorCode: Type.Optional(Type.String({ minLength: 1, maxLength: 128 }))
  },
  { additionalProperties: false }
);

const RetrievalFusionTraceItemSchema = Type.Object(
  {
    rank: PositiveIntegerSchema,
    score: Type.Number(),
    evidenceId: IdentifierSchema,
    documentId: UuidSchema,
    unitId: IdentifierSchema,
    matchedRoutes: Type.Array(RouteSchema, {
      minItems: 1,
      maxItems: RETRIEVAL_ROUTES.length,
      uniqueItems: true
    })
  },
  { additionalProperties: false }
);

export const RetrievalSearchTraceSchema = Type.Object(
  {
    schemaVersion: Type.Literal("1.0"),
    searchId: IdentifierSchema,
    purpose: Type.Union([Type.Literal("probe"), Type.Literal("follow_up")]),
    totalDurationMs: Type.Integer({ minimum: 0 }),
    truncated: Type.Boolean(),
    topology: Type.Object(
      {
        durationMs: Type.Integer({ minimum: 0 }),
        leafConceptizationResultId: Type.Union([UuidSchema, Type.Null()]),
        hierarchySnapshotId: Type.Union([UuidSchema, Type.Null()])
      },
      { additionalProperties: false }
    ),
    scope: Type.Object(
      {
        durationMs: Type.Integer({ minimum: 0 }),
        authorizedDocumentCount: Type.Integer({ minimum: 0 }),
        currentDocumentCount: Type.Integer({ minimum: 0 }),
        currentUnitizationGenerationCount: Type.Integer({ minimum: 0 })
      },
      { additionalProperties: false }
    ),
    routes: Type.Array(RetrievalSearchRouteTraceSchema, {
      minItems: RETRIEVAL_ROUTES.length,
      maxItems: RETRIEVAL_ROUTES.length
    }),
    revalidation: Type.Object(
      {
        durationMs: Type.Integer({ minimum: 0 }),
        candidateHitCount: Type.Integer({ minimum: 0 }),
        currentHitCount: Type.Integer({ minimum: 0 })
      },
      { additionalProperties: false }
    ),
    fusion: Type.Object(
      {
        durationMs: Type.Integer({ minimum: 0 }),
        candidateCount: Type.Integer({ minimum: 0 }),
        evidenceCount: Type.Integer({ minimum: 0 }),
        routeAwareRrfK: PositiveIntegerSchema,
        resultBudget: RetrievalResultBudgetSchema,
        items: Type.Array(RetrievalFusionTraceItemSchema, { maxItems: 200 }),
        truncated: Type.Boolean()
      },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);

export const RetrievalSearchTraceResultSchema = Type.Object(
  {
    query: Type.String({ minLength: 1, maxLength: 4096 }),
    result: RetrievalSearchResultSchema,
    trace: RetrievalSearchTraceSchema
  },
  { additionalProperties: false }
);

export const RetrievalSearchLimitsSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_RETRIEVAL_SCHEMA_VERSION),
    knowledgeBaseId: UuidSchema,
    maxNeedsPerFollowUp: PositiveIntegerSchema,
    maxResultBudget: RetrievalResultBudgetSchema,
    timeoutMs: PositiveIntegerSchema
  },
  { additionalProperties: false }
);

export const RetrievalErrorResponseSchema = Type.Object(
  {
    error: Type.Object(
      {
        code: Type.String({ minLength: 1, maxLength: 128 }),
        message: Type.String({ minLength: 1, maxLength: 4096 }),
        retryable: Type.Boolean(),
        requestId: IdentifierSchema,
        traceId: IdentifierSchema
      },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);

export type RetrievalResultBudget = Static<typeof RetrievalResultBudgetSchema>;
export type RetrievalExecutionConfig = Static<typeof RetrievalExecutionConfigSchema>;
export type RetrievalDeploymentHardCaps = Static<typeof RetrievalDeploymentHardCapsSchema>;
export type RetrievalRuntimeConfig = Static<typeof RetrievalRuntimeConfigSchema>;
export type RetrievalProbeRequest = Static<typeof RetrievalProbeRequestSchema>;
export type RetrievalFollowUpRequest = Static<typeof RetrievalFollowUpRequestSchema>;
export type RetrievalSearchRequest = Static<typeof RetrievalSearchRequestSchema>;
export type RetrievalSourceSpan = Static<typeof RetrievalSourceSpanSchema>;
export type RetrievalEvidence = Static<typeof RetrievalEvidenceSchema>;
export type RetrievalCitation = Static<typeof RetrievalCitationSchema>;
export type RetrievalChannelResult = Static<typeof RetrievalChannelResultSchema>;
export type RetrievalSearchResult = Static<typeof RetrievalSearchResultSchema>;
export type RetrievalSearchTraceInput = Static<typeof RetrievalSearchTraceInputSchema>;
export type RetrievalSearchTrace = Static<typeof RetrievalSearchTraceSchema>;
export type RetrievalSearchTraceResult = Static<typeof RetrievalSearchTraceResultSchema>;
export type RetrievalSearchLimits = Static<typeof RetrievalSearchLimitsSchema>;
export type RetrievalErrorResponse = Static<typeof RetrievalErrorResponseSchema>;
